import * as THREE from "three";
import { Input } from "./Input";
import type { Scene } from "./Scene";
import { UI } from "../ui/UI";
import { AudioEngine } from "../audio/AudioEngine";
import { RNG } from "./RNG";

export interface GameOptions {
  canvas: HTMLCanvasElement;
  uiRoot: HTMLElement;
  /**
   * Vertical resolution the 3D view renders at before being stretched to fit.
   * Low values give the chunky look the genre is known for.
   */
  virtualHeight?: number;
  seed?: number;
}

/**
 * Owns the renderer, the loop, and the scene stack. Everything a scene needs
 * from the outside world hangs off this object.
 */
export class Game {
  readonly renderer: THREE.WebGLRenderer;
  readonly input: Input;
  readonly ui: UI;
  readonly audio: AudioEngine;
  readonly rng: RNG;

  private readonly canvas: HTMLCanvasElement;
  private readonly virtualHeight: number;
  private stack: Scene[] = [];
  /** Set while a scene transition is in flight; suppresses updates. */
  private busy = false;
  private running = false;
  private lastTime = 0;
  private frameHandle = 0;

  constructor(options: GameOptions) {
    this.canvas = options.canvas;
    this.virtualHeight = options.virtualHeight ?? 480;
    this.rng = new RNG(options.seed ?? Date.now());

    this.renderer = new THREE.WebGLRenderer({
      canvas: options.canvas,
      antialias: false,
      powerPreference: "high-performance",
    });
    this.renderer.setClearColor(0x0b0b18, 1);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.input = new Input(options.canvas);
    this.audio = new AudioEngine();
    this.ui = new UI(options.uiRoot, this.input, {
      onMove: () => this.audio.play("cursor"),
      onSelect: () => this.audio.play("confirm"),
      onCancel: () => this.audio.play("cancel"),
    });

    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  // ------------------------------------------------------------- scene stack

  get current(): Scene | undefined {
    return this.stack[this.stack.length - 1];
  }

  async push(scene: Scene): Promise<void> {
    this.busy = true;
    try {
      this.current?.pause();
      scene.game = this;
      this.stack.push(scene);
      this.applyViewport(scene);
      await scene.enter();
    } finally {
      this.busy = false;
      // A keystroke that happened during the transition should not be seen by
      // the scene that just opened.
      this.input.clearPressed();
    }
  }

  async pop(): Promise<void> {
    if (this.stack.length === 0) return;
    this.busy = true;
    try {
      const scene = this.stack.pop()!;
      scene.exit();
      this.current?.resume();
    } finally {
      this.busy = false;
      this.input.clearPressed();
    }
  }

  /** Pops the top scene and pushes another in its place. */
  async replace(scene: Scene): Promise<void> {
    if (this.stack.length > 0) {
      const top = this.stack.pop()!;
      top.exit();
    }
    await this.push(scene);
  }

  // -------------------------------------------------------------- main loop

  start(initial: Scene): void {
    if (this.running) return;
    this.running = true;
    void this.push(initial);
    this.lastTime = performance.now();
    this.frameHandle = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frameHandle);
  }

  private readonly tick = (now: number): void => {
    this.frameHandle = requestAnimationFrame(this.tick);

    // Clamp: a backgrounded tab produces a huge dt that would teleport actors
    // through walls.
    const dt = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;

    // Mute is global: it works during dialogue, battles and transitions alike.
    if (this.input.consume("mute")) {
      this.ui.setHint(this.audio.toggleMute() ? "sound off" : "sound on");
    }

    if (!this.busy) {
      this.current?.update(dt);
      this.ui.update(dt);
    }
    this.draw();
    this.input.endFrame();
  };

  private draw(): void {
    if (this.stack.length === 0) return;

    // Render from the lowest scene that is still visible: an overlay scene lets
    // whatever is underneath show through.
    let from = this.stack.length - 1;
    while (from > 0 && !this.stack[from].opaque) from--;

    this.renderer.clear();
    const previousAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = false;
    for (let i = from; i < this.stack.length; i++) {
      if (i > from) this.renderer.clearDepth();
      this.stack[i].render(this.renderer);
    }
    this.renderer.autoClear = previousAutoClear;
  }

  // -------------------------------------------------------------- viewport

  private readonly handleResize = (): void => {
    const cssWidth = this.canvas.clientWidth || window.innerWidth;
    const cssHeight = this.canvas.clientHeight || window.innerHeight;

    // Render at (roughly) virtualHeight and let CSS scale the canvas up, which
    // is both faster and correctly crunchy.
    const scale = Math.max(1, cssHeight / this.virtualHeight);
    const width = Math.max(1, Math.round(cssWidth / scale));
    const height = Math.max(1, Math.round(cssHeight / scale));

    this.renderer.setPixelRatio(1);
    this.renderer.setSize(width, height, false);
    for (const scene of this.stack) scene.resize(width, height);
  };

  private applyViewport(scene: Scene): void {
    const size = new THREE.Vector2();
    this.renderer.getSize(size);
    scene.resize(size.x, size.y);
  }

  /** Waits n seconds, respecting nothing in particular. Handy in cutscenes. */
  wait(seconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
  }
}
